// Angular
import {
	CommonModule,
	HashLocationStrategy,
	LocationStrategy,
} from "@angular/common";
import { CUSTOM_ELEMENTS_SCHEMA, LOCALE_ID, NgModule } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { BrowserModule, Title } from "@angular/platform-browser";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";

// Shared
import { SharedModule } from "./shared.module";
import { NameCasePipe } from "./_core/pipes/name-case.pipe";
import { EmptyStateComponent } from "./_core/components/empty-state/empty-state.component";
import { HistorialComponent } from "./modules/shared/components/historial/historial.component";
import { EnergiaTabComponent } from "./modules/shared/components/energia-tab/energia-tab.component";
import { DsSortTableDirective } from "./_core/directives/ds-sort-table.directive";
import { UtilsMenuComponent } from "./modules/shared/components/utils-menu/utils-menu.component";
import { SharedFormControlsModule } from "./modules/form-controls/shared-form-controls.module";
import { SharedContactsModule } from "./modules/contacts/shared-contacts.module";

// Core
import {
	MSAL_GUARD_CONFIG,
	MSAL_INSTANCE,
	MSAL_INTERCEPTOR_CONFIG,
	MsalBroadcastService,
	MsalGuard,
	MsalGuardConfiguration,
	MsalInterceptor,
	MsalInterceptorConfiguration,
	MsalModule,
	MsalService,
} from "@azure/msal-angular";
import { AppComponent } from "./app.component";

// Pages
import { HomeComponent } from "./modules/main/pages/home/home.page";

// Components
import { ScrollingModule } from "@angular/cdk/scrolling";
import { HTTP_INTERCEPTORS, HttpClient } from "@angular/common/http";
import {
	BrowserCacheLocation,
	IPublicClientApplication,
	InteractionType,
	LogLevel,
	PublicClientApplication,
} from "@azure/msal-browser";
import { TranslateLoader, TranslateModule } from "@ngx-translate/core";
import { TranslateHttpLoader } from "@ngx-translate/http-loader";
import { environment } from "src/environments/environment";
import { AuthGuard } from "./_core/interceptors/auth-guard";
import { AuthInterceptor } from "./_core/interceptors/auth.interceptor";
import { ErrorInterceptor } from "./_core/interceptors/error.interceptor";
import { LoaderInterceptor } from "./_core/interceptors/loader.interceptor";
import { BrandTitleService } from "./_core/providers/brand-title.service";
import { GlobalService } from "./_core/providers/global.service";
import { LocaleService } from "./_core/providers/locale.service";
import { AppRoutingModule } from "./app-routing.module";
import { CentroDesignDataCardComponent } from "./modules/main/components/centros/centro-design-data-card/centro-design-data-card.component";
import { CentroDetailsCardComponent } from "./modules/main/components/centros/centro-details-card/centro-details-card.component";
import { CentroMapCardComponent } from "./modules/main/components/centros/centro-map-card/centro-map-card.component";
import { CentroOperationDataCardComponent } from "./modules/main/components/centros/centro-operation-data-card/centro-operation-data-card.component";
import { CentroUserReportCardComponent } from "./modules/main/components/centros/centro-user-report-card/centro-user-report-card.component";
import { PersonComponent } from "./modules/main/components/person/person.component";
import { AdminPageComponent } from "./modules/main/pages/admin/admin.page";
import { PositionsPageComponent } from "./modules/main/pages/positions/positions.page";
import { AdminHomePageComponent } from "./modules/main/pages/admin-home/admin-home.page";
import { RolesPageComponent } from "./modules/main/pages/roles/roles.page";
import { CentroPageComponent } from "./modules/main/pages/centro/centro.page";
import { CodesPopoverComponent } from "./modules/contracts/components/codes-popover/codes-popover.component";
import { SortPipe } from "./modules/shared/directives/sort-pipe";
import { NoPermissionsComponent } from "./modules/shared/no-permissions/no-permissions.component";
import { CentrosListComponent } from "./modules/main/pages/centros/centros-list.page";
import { SharedMapsModule } from "./modules/main/components/maps/shared-maps.module";

const isIE =
	window.navigator.userAgent.indexOf("MSIE ") > -1 ||
	window.navigator.userAgent.indexOf("Trident/") > -1;

export function loggerCallback(logLevel: LogLevel, message: string) {
	if (!environment.production) {
		console.log(message);
	}
}

export function MSALInstanceFactory(): IPublicClientApplication {
	return new PublicClientApplication({
		auth: {
			clientId: environment.azureAD.clientId,
			authority: environment.azureAD.authority,
			redirectUri: environment.azureAD.redirectUri,
			postLogoutRedirectUri: environment.azureAD.postLogoutRedirectUri,
			navigateToLoginRequestUrl: true,
		},
		cache: {
			cacheLocation: BrowserCacheLocation.LocalStorage,
			storeAuthStateInCookie: isIE,
		},
		system: {
			allowRedirectInIframe: true,
			loggerOptions: {
				loggerCallback: () => {},
				logLevel: LogLevel.Info,
				piiLoggingEnabled: false,
			},
		},
	});
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
	return {
		interactionType: InteractionType.Redirect,
		protectedResourceMap: new Map([
			[
				"https://graph.microsoft.com/v1.0/me/photo/$value",
				["user.read.all"],
			],
			["https://graph.microsoft.com/v1.0/me", ["user.read"]],
			["https://graph.microsoft.com/v1.0/users", ["contacts.read"]],
			// ['https://api.powerbi.com/v1.0/myorg', ['Report.ReadWrite.All', 'Report.Read.All']]
		]),
	};
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
	return {
		interactionType: InteractionType.Redirect,
		authRequest: {
			// scopes: ['user.read', 'contacts.read', 'Report.ReadWrite.All', 'Report.Read.All']
			scopes: ["user.read", "contacts.read"],
		},
		loginFailedRoute: "/login-failed",
	};
}

@NgModule({
	declarations: [
		AppComponent,
		NoPermissionsComponent,
		HomeComponent,
		PersonComponent,
		CentroPageComponent,
		CentroDetailsCardComponent,
		CentroUserReportCardComponent,
		CentroDesignDataCardComponent,
		CentroOperationDataCardComponent,
		AdminPageComponent,
		PositionsPageComponent,
		AdminHomePageComponent,
		RolesPageComponent,
		CentrosListComponent,
		SortPipe,
	],
	imports: [
		CentroMapCardComponent,
		BrowserModule,
		CommonModule,
		FormsModule,
		ScrollingModule,
		BrowserAnimationsModule,
		AppRoutingModule,
		SharedModule,
		NameCasePipe,
		EmptyStateComponent,
		HistorialComponent,
		EnergiaTabComponent,
		CodesPopoverComponent,
		DsSortTableDirective,
		UtilsMenuComponent,
		SharedFormControlsModule,
		SharedContactsModule,
		SharedMapsModule,
		TranslateModule.forRoot({
			loader: {
				provide: TranslateLoader,
				useFactory: HttpLoaderFactory,
				deps: [HttpClient],
			},
		}),
		MsalModule.forRoot(
			new PublicClientApplication({
				auth: {
					clientId: environment.azureAD.clientId,
					authority: environment.azureAD.authority,
					redirectUri: environment.azureAD.redirectUri,
					postLogoutRedirectUri:
						environment.azureAD.postLogoutRedirectUri,
					navigateToLoginRequestUrl: true,
				},
				cache: {
					cacheLocation: BrowserCacheLocation.LocalStorage,
					storeAuthStateInCookie: isIE,
				},
				system: {
					allowRedirectInIframe: true,
					loggerOptions: {
						loggerCallback: () => {},
						logLevel: LogLevel.Info,
						piiLoggingEnabled: false,
					},
				},
			}),
			{
				interactionType: InteractionType.Redirect,
			},
			{
				interactionType: InteractionType.Redirect,
				protectedResourceMap: new Map([
					[
						"https://graph.microsoft.com/v1.0/me/photo/$value",
						["user.read.all"],
					],
					["https://graph.microsoft.com/v1.0/me", ["user.read"]],
					[
						"https://graph.microsoft.com/v1.0/users",
						["contacts.read"],
					],
				]),
			},
		),
	],
	exports: [AppRoutingModule],
	providers: [
		// Título del navegador centralizado: "CENTROS" en landing, "CENTROS | Página" en el resto.
		{
			provide: Title,
			useClass: BrandTitleService,
		},
		{
			provide: LocationStrategy,
			useClass: HashLocationStrategy,
		},
		GlobalService,
		{
			provide: MSAL_INSTANCE,
			useFactory: MSALInstanceFactory,
		},
		{
			provide: MSAL_GUARD_CONFIG,
			useFactory: MSALGuardConfigFactory,
		},
		{
			provide: MSAL_INTERCEPTOR_CONFIG,
			useFactory: MSALInterceptorConfigFactory,
		},
		AuthGuard,
		{
			// provide: LOCALE_ID,
			// deps: [LocaleService],
			// useValue: (localeService : LocaleService) => localeService.getLanguage()
			provide: LOCALE_ID,
			useFactory: (localeService: LocaleService) => {
				return localeService.getLanguage();
			},
			deps: [LocaleService],
		},
		{ provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
		{ provide: HTTP_INTERCEPTORS, useClass: MsalInterceptor, multi: true },
		{ provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
		{
			provide: HTTP_INTERCEPTORS,
			useClass: LoaderInterceptor,
			multi: true,
		},
		MsalService,
		MsalGuard,
		MsalBroadcastService,
	],
	bootstrap: [AppComponent],
	schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class AppModule {}

// AoT requires an exported function for factories
export function HttpLoaderFactory(http: HttpClient) {
	return new TranslateHttpLoader(http, "./assets/i18n/", ".json");
}
